import { useCallback, useRef, useState } from 'react';
import { bomClient } from '../client/bomClient';
import { useRFQ } from '../contexts/RFQContext';
import { DiscoveryRefusal, DiscoveryRequirements, DiscoveryStarted } from '../types/discovery.types';
import { useRecordRobbieAction } from '../contexts/RobbieUiContext';
import { robbieActionText } from '../utils/robbieActivity';
import { discoveryErrorCode, discoveryErrorMessage, readDiscoveryRefusal } from './bomCredits';
import { invalidateOrgCredits } from './useOrgCredits';

/**
 * The step 2 → 3 exit: the moment the BOM is charged for supplier discovery.
 *
 * Continue sends every line. When the org is short (402), nothing was written,
 * and the answer decides what the buyer sees next: a picker capped at the
 * `affordable` lines when the balance covers at least one, the refused card
 * when it covers none (or UMS did not say what the balance is, so there is no
 * cap to offer). The picker sends its `componentIds`; the lines left out are
 * removed from the BOM by the backend. A second 402 from the picker (the
 * balance moved meanwhile) refreshes the refusal and keeps it open.
 *
 * Contract: docs/flows/02-bom-edit-and-discovery-charge.md §3.
 */

interface Options {
  rfqId: string;
  /** Runs once discovery has started (202): refetch, move to step 3. */
  onStarted: (result?: DiscoveryStarted) => Promise<void> | void;
}

const canPick = (refusal: DiscoveryRefusal) => (refusal.affordable ?? 0) > 0;

export const useDiscoveryExit = ({ rfqId, onStarted }: Options) => {
  const { fetchRFQ } = useRFQ();
  const recordAction = useRecordRobbieAction();
  const [starting, setStarting] = useState(false);
  const [refusal, setRefusal] = useState<DiscoveryRefusal | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** Shown on the step: a failed Continue, or a picker closed by a changed BOM. */
  const [error, setError] = useState<string | null>(null);
  /** Shown inside the picker, which stays open so the pick is not lost. */
  const [pickerError, setPickerError] = useState<string | null>(null);
  // The criteria saved with the first attempt ride along with the picker's.
  const requirementsRef = useRef<DiscoveryRequirements | undefined>(undefined);

  /**
   * Reload the RFQ without moving the wizard. Best effort: a failed reload
   * must never swallow the refusal it was fetched for, so the picker opens
   * with the lines already in the store.
   */
  const refreshQuietly = useCallback(async () => {
    try {
      await fetchRFQ(rfqId, true, true);
    } catch (refreshError) {
      // No structured logger in the browser yet; this is the one trace of it.
      // eslint-disable-next-line no-console
      console.warn('[discovery] RFQ refresh after a refusal failed; using the BOM already loaded', refreshError);
    }
  }, [fetchRFQ, rfqId]);

  const started = useCallback(async (result?: DiscoveryStarted) => {
    if (result) {
      recordAction(
        'startDiscovery',
        robbieActionText.startedDiscovery(
          result.totalComponents,
          result.discoveryCharge ? result.discoveryCharge.credits : null,
          result.removedComponentIds?.length ?? 0
        )
      );
    }
    invalidateOrgCredits();
    setRefusal(null);
    setPickerOpen(false);
    await onStarted(result);
  }, [onStarted, recordAction]);

  /** Continue: every line on the BOM. */
  const begin = useCallback(
    async (requirements: DiscoveryRequirements) => {
      requirementsRef.current = requirements;
      setError(null);
      setPickerError(null);
      setRefusal(null);
      setStarting(true);
      try {
        await started(await bomClient.analyzeBOM(rfqId, { requirements }));
      } catch (err) {
        const short = readDiscoveryRefusal(err);
        if (!short) {
          setError(discoveryErrorMessage(err));
          return;
        }
        // The picker lists the lines the server will check the pick against.
        if (canPick(short)) await refreshQuietly();
        setRefusal(short);
        setPickerOpen(canPick(short));
      } finally {
        setStarting(false);
      }
    },
    [rfqId, started, refreshQuietly]
  );

  /** The picker's Start: only the ticked lines; the rest leave the BOM. */
  const startSelected = useCallback(
    async (componentIds: string[]) => {
      setPickerError(null);
      setStarting(true);
      try {
        await started(await bomClient.analyzeBOM(rfqId, { componentIds, requirements: requirementsRef.current }));
      } catch (err) {
        const short = readDiscoveryRefusal(err);
        if (short) {
          setRefusal(short);
          if (!canPick(short)) setPickerOpen(false);
          return;
        }
        if (discoveryErrorCode(err) === 'UNKNOWN_COMPONENT') {
          // The fix is on the step (reopen the BOM), not in this list.
          setPickerOpen(false);
          setRefusal(null);
          setError(discoveryErrorMessage(err));
          await refreshQuietly();
          return;
        }
        setPickerError(discoveryErrorMessage(err));
      } finally {
        setStarting(false);
      }
    },
    [rfqId, started, refreshQuietly]
  );

  const cancelPicker = useCallback(() => {
    setPickerOpen(false);
    setPickerError(null);
    setRefusal(null);
  }, []);

  return {
    starting,
    /** The current 402, while the picker or the refused card shows it. */
    refusal,
    pickerOpen,
    /** The refusal to render as a card on the step: no picker to offer. */
    refused: refusal && !pickerOpen ? refusal : null,
    error,
    dismissError: () => setError(null),
    pickerError,
    begin,
    startSelected,
    cancelPicker,
  };
};

export default useDiscoveryExit;
